import { useEffect, useState } from "react";
import axios from "axios";
import { MB_PALETTE } from "./constants";
import { useProduct } from "@/context/ProductContext";
import { CaretRight } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DynamicBuckets = ({ countries, onSelect }) => {
  const { productId } = useProduct();
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    axios.get(`${API}/buckets/dynamic/${productId}`).then((r) => setData(r.data)).catch(console.error);
  }, [productId]);
  if (!data) return <div data-testid="loading-state" className="py-16 text-center text-slate-400 text-sm">Clustering markets…</div>;

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-500">
        Buckets are discovered by AI clustering on {Object.keys(data.dimensions).length} behavioral dimensions — never manually assigned. Markets can belong to multiple buckets.
      </p>
      <div data-testid="dynamic-buckets" className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {data.buckets.map((b, i) => {
          const color = MB_PALETTE[i % MB_PALETTE.length];
          return (
            <div key={b.bucket_id} data-testid={`dynamic-bucket-${b.bucket_id}`} className="bg-white border border-slate-200 stagger-in" style={{ borderTop: `3px solid ${color}`, animationDelay: `${i * 60}ms` }}>
              <div className="p-5 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <p className="overline-label" style={{ color }}>{b.bucket_id}</p>
                  <span className="font-mono-data text-xs text-slate-400">cohesion {b.similarity_score}</span>
                </div>
                <h3 className="font-heading font-bold text-base tracking-tight mt-1.5 leading-snug">{b.name}</h3>
                <p className="text-xs text-slate-600 leading-relaxed mt-2">{b.evidence_philosophy}</p>
              </div>
              <div className="p-5 space-y-3 border-b border-slate-200 text-xs text-slate-600">
                <div>
                  <p className="overline-label text-slate-400 mb-1">Typical Evidence Package</p>
                  <ul className="space-y-1">
                    {b.typical_evidence_package.map((x) => <li key={x} className="flex gap-1.5"><span className="mt-1.5 h-1 w-1 shrink-0" style={{ background: color }} />{x}</li>)}
                  </ul>
                </div>
                <p><span className="font-semibold text-slate-700">Localisation:</span> {b.typical_localisation_requirements}</p>
                <p><span className="font-semibold text-slate-700">Submissions:</span> {b.typical_submission_characteristics}</p>
              </div>
              <div className="p-2">
                {b.members.map((m) => (
                  <button key={m.id} data-testid={`dyn-bucket-country-${b.bucket_id}-${m.id}`}
                    onClick={() => { const c = countries.find((x) => x.id === m.id); if (c) onSelect(c); }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-slate-50 text-left group">
                    <span className="text-lg">{m.flag}</span>
                    <span className="text-sm font-semibold text-slate-900 flex-1">{m.name}</span>
                    {m.primary && <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 text-white" style={{ background: color }}>Primary</span>}
                    <span className="font-mono-data text-xs text-slate-500">{Math.round(m.membership * 100)}%</span>
                    <CaretRight size={13} className="text-slate-300 group-hover:text-slate-900" />
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
